/**
 * O VOCABULÁRIO DO BANCO — cada substantivo, adjetivo e verbo que os itens usam para
 * nomear coisas, declarado uma vez com o seu gênero e a sua flexão.
 *
 * POR QUE ISTO EXISTE. Um gerador monta frases juntando pedaços, e o pedaço que varia
 * com a seed carrega gênero e número que o TypeScript não enxerga: `ctx.pergunta` é
 * `string`, e `'consultas'` e `'alunos'` são o mesmo tipo. Por isso
 * `Quantos ${ctx.pergunta}` compila — e produz **"Quantos consultas escolheram
 * Rotina?"** no comando, que é a frase mais visível do item.
 *
 * A correção não é escrever com mais cuidado. Uma revisão de 168 variantes achou trinta
 * defeitos assim, e o banco escrevia concordância à mão em 67 ternários espalhados por
 * 26 arquivos. A correção é **o substantivo deixar de ser `string`**: quando o contexto
 * declara `conta: 'consulta'` — uma chave daqui, não um texto —, a frase não pode mais
 * ser montada por concatenação, e quem a monta sabe que `consulta` é feminino.
 *
 * TRÊS LIMITES DE ESCOPO, de propósito:
 *
 *  1. **Tabela, não analisador.** Nada de inferir gênero pela terminação: `-ão` faz
 *     `mão/mãos`, `pão/pães` e `coração/corações`, e regra nenhuma acerta os três. As
 *     formas são escritas à mão, uma vez.
 *  2. **A prosa continua prosa.** O texto de abertura de cada contexto não vira função:
 *     ele não varia com a seed. Quem garante que a prosa não contradiga o elenco é a
 *     auditoria, não a geração.
 *  3. **Dado puro, sem importações.** Este arquivo não importa nada, para que `pt.ts` e
 *     a auditoria o consumam sem ciclo.
 *
 * A CHAVE É SEM ACENTO (`praca`, `chao`, `mes`); a forma escrita carrega o acento.
 * Identificador em ASCII mantém busca, autocomplete e diff simples, e o texto visível
 * nunca sai da chave.
 */

export type Genero = 'm' | 'f';
export type Numero = 'sg' | 'pl';

/**
 * Nomes que disputam o mesmo papel dentro de um item.
 *
 * Existe para um defeito específico: o item da balança chamava o mesmo objeto de
 * "pote" no enunciado, "saco" na resolução e "x" no rótulo; o da urna dizia "saco" no
 * texto e "urna" na dica. Com o grupo declarado, a auditoria vê dois nomes do mesmo
 * papel no mesmo item e acusa — coisa que nenhuma regra sobre texto solto conseguiria
 * formular.
 */
export type Grupo =
  | 'recipiente'
  | 'pessoa-pesquisada'
  | 'peca-sorteada'
  | 'superficie'
  | 'registro'
  | 'cor-clara'
  | 'cor-escura';

export interface Nome {
  readonly sg: string;
  readonly pl: string;
  readonly g: Genero;
  /**
   * A forma colide com outra classe de palavra — "vendas" é substantivo e é verbo,
   * "conta" é substantivo e é verbo. A auditoria relata, mas não assevera, quando a
   * palavra envolvida é destas.
   */
  readonly ambiguo?: true;
  readonly grupo?: Grupo;
}

/**
 * As entradas saíram do próprio banco: os pares singular/plural que já estavam escritos
 * à mão nos 67 ternários, mais os substantivos declarados nos contextos dos itens. Não
 * há entrada inventada "por precaução" — entrada sem uso é peso morto, e a regra da
 * auditoria que lista substantivo fora do léxico é quem faz a tabela crescer, com prova.
 */
export const NOMES = {
  // --- quem é contado numa pesquisa -----------------------------------------
  aluno: { sg: 'aluno', pl: 'alunos', g: 'm', grupo: 'pessoa-pesquisada' },
  morador: { sg: 'morador', pl: 'moradores', g: 'm', grupo: 'pessoa-pesquisada' },
  assinante: { sg: 'assinante', pl: 'assinantes', g: 'm', grupo: 'pessoa-pesquisada' },
  participante: { sg: 'participante', pl: 'participantes', g: 'm', grupo: 'pessoa-pesquisada' },
  visitante: { sg: 'visitante', pl: 'visitantes', g: 'm', grupo: 'pessoa-pesquisada' },
  entregador: { sg: 'entregador', pl: 'entregadores', g: 'm', grupo: 'pessoa-pesquisada' },
  pessoa: { sg: 'pessoa', pl: 'pessoas', g: 'f', grupo: 'pessoa-pesquisada' },

  // --- registros e ocorrências ----------------------------------------------
  consulta: { sg: 'consulta', pl: 'consultas', g: 'f', grupo: 'registro' },
  atendimento: { sg: 'atendimento', pl: 'atendimentos', g: 'm', grupo: 'registro' },
  visita: { sg: 'visita', pl: 'visitas', g: 'f', grupo: 'registro' },
  encomenda: { sg: 'encomenda', pl: 'encomendas', g: 'f' },
  venda: { sg: 'venda', pl: 'vendas', g: 'f', ambiguo: true },
  conta: { sg: 'conta', pl: 'contas', g: 'f', ambiguo: true },

  // --- recipientes, que é onde o banco mais se contradiz ---------------------
  saco: { sg: 'saco', pl: 'sacos', g: 'm', grupo: 'recipiente' },
  saquinho: { sg: 'saquinho', pl: 'saquinhos', g: 'm', grupo: 'recipiente' },
  pote: { sg: 'pote', pl: 'potes', g: 'm', grupo: 'recipiente' },
  urna: { sg: 'urna', pl: 'urnas', g: 'f', grupo: 'recipiente' },
  caixa: { sg: 'caixa', pl: 'caixas', g: 'f', grupo: 'recipiente' },

  // --- o que se sorteia ------------------------------------------------------
  bola: { sg: 'bola', pl: 'bolas', g: 'f', grupo: 'peca-sorteada' },
  bolinha: { sg: 'bolinha', pl: 'bolinhas', g: 'f', grupo: 'peca-sorteada' },
  ficha: { sg: 'ficha', pl: 'fichas', g: 'f', grupo: 'peca-sorteada' },
  peso: { sg: 'peso', pl: 'pesos', g: 'm' },

  // --- superfícies a cobrir --------------------------------------------------
  chao: { sg: 'chão', pl: 'chãos', g: 'm', grupo: 'superficie' },
  parede: { sg: 'parede', pl: 'paredes', g: 'f', grupo: 'superficie' },
  praca: { sg: 'praça', pl: 'praças', g: 'f', grupo: 'superficie' },

  // --- objetos da figura -----------------------------------------------------
  reta: { sg: 'reta', pl: 'retas', g: 'f' },
  eixo: { sg: 'eixo', pl: 'eixos', g: 'm' },
  coluna: { sg: 'coluna', pl: 'colunas', g: 'f' },
  fatia: { sg: 'fatia', pl: 'fatias', g: 'f' },
  setor: { sg: 'setor', pl: 'setores', g: 'm' },
  vertice: { sg: 'vértice', pl: 'vértices', g: 'm' },
  lado: { sg: 'lado', pl: 'lados', g: 'm' },
  diagonal: { sg: 'diagonal', pl: 'diagonais', g: 'f' },
  angulo: { sg: 'ângulo', pl: 'ângulos', g: 'm' },
  poligono: { sg: 'polígono', pl: 'polígonos', g: 'm' },
  triangulo: { sg: 'triângulo', pl: 'triângulos', g: 'm' },
  quadrado: { sg: 'quadrado', pl: 'quadrados', g: 'm' },
  quadradinho: { sg: 'quadradinho', pl: 'quadradinhos', g: 'm' },
  ponto: { sg: 'ponto', pl: 'pontos', g: 'm' },
  linha: { sg: 'linha', pl: 'linhas', g: 'f' },
  parte: { sg: 'parte', pl: 'partes', g: 'f', ambiguo: true },
  classe: { sg: 'classe', pl: 'classes', g: 'f' },
  canteiro: { sg: 'canteiro', pl: 'canteiros', g: 'm' },
  prato: { sg: 'prato', pl: 'pratos', g: 'm' },

  // --- coisas contadas no enunciado -----------------------------------------
  livro: { sg: 'livro', pl: 'livros', g: 'm' },
  bicicleta: { sg: 'bicicleta', pl: 'bicicletas', g: 'f' },
  grao: { sg: 'grão', pl: 'grãos', g: 'm' },
  parafuso: { sg: 'parafuso', pl: 'parafusos', g: 'm' },
  nota: { sg: 'nota', pl: 'notas', g: 'f' },
  grau: { sg: 'grau', pl: 'graus', g: 'm' },
  mes: { sg: 'mês', pl: 'meses', g: 'm' },
  rodada: { sg: 'rodada', pl: 'rodadas', g: 'f' },
  // Entrou por evidência: "70 ÷ 60 ≈ 1,2 vez" passou despercebido porque eu havia
  // classificado "vez" como palavra de estrutura. É contável, e é das que mais
  // aparecem depois de número decimal — onde o plural surpreende quem escreve.
  vez: { sg: 'vez', pl: 'vezes', g: 'f' },
  loja: { sg: 'loja', pl: 'lojas', g: 'f' },
  museu: { sg: 'museu', pl: 'museus', g: 'm' },
  fabrica: { sg: 'fábrica', pl: 'fábricas', g: 'f' },
  modelo: { sg: 'modelo', pl: 'modelos', g: 'm' },
  // Entraram pela lista de compras da auditoria (R6): contados nos itens, e ainda fora
  // da tabela. Declarados, a próxima frase que os contar já nasce concordando.
  unidade: { sg: 'unidade', pl: 'unidades', g: 'f' },
  resultado: { sg: 'resultado', pl: 'resultados', g: 'm' },
  peca: { sg: 'peça', pl: 'peças', g: 'f' },
  cubinho: { sg: 'cubinho', pl: 'cubinhos', g: 'm' },
  valor: { sg: 'valor', pl: 'valores', g: 'm' },
  cruzamento: { sg: 'cruzamento', pl: 'cruzamentos', g: 'm' },
  segmento: { sg: 'segmento', pl: 'segmentos', g: 'm' },
  dado: { sg: 'dado', pl: 'dados', g: 'm' },
  giro: { sg: 'giro', pl: 'giros', g: 'm' },
  minuto: { sg: 'minuto', pl: 'minutos', g: 'm' },
  hora: { sg: 'hora', pl: 'horas', g: 'f' },
  ano: { sg: 'ano', pl: 'anos', g: 'm' },
  residencia: { sg: 'residência', pl: 'residências', g: 'f' },
  equipe: { sg: 'equipe', pl: 'equipes', g: 'f' },

  // --- quem publica a afirmação que o item manda julgar ------------------------
  // Entraram porque o retorno dizia "a manchete" em todas as variantes, também quando
  // o texto julgado era uma propaganda, um informativo de escola ou um relatório.
  manchete: { sg: 'manchete', pl: 'manchetes', g: 'f' },
  texto: { sg: 'texto', pl: 'textos', g: 'm' },
  conclusao: { sg: 'conclusão', pl: 'conclusões', g: 'f' },
  informativo: { sg: 'informativo', pl: 'informativos', g: 'm' },
  propaganda: { sg: 'propaganda', pl: 'propagandas', g: 'f' },

  // --- vocabulário do próprio app -------------------------------------------
  questao: { sg: 'questão', pl: 'questões', g: 'f' },
  resposta: { sg: 'resposta', pl: 'respostas', g: 'f' },
  item: { sg: 'item', pl: 'itens', g: 'm' },
  movimento: { sg: 'movimento', pl: 'movimentos', g: 'm' },
  estrela: { sg: 'estrela', pl: 'estrelas', g: 'f' },
  figura: { sg: 'figura', pl: 'figuras', g: 'f' },
  casa: { sg: 'casa', pl: 'casas', g: 'f' },
  etapa: { sg: 'etapa', pl: 'etapas', g: 'f' },
  caso: { sg: 'caso', pl: 'casos', g: 'm' },
  // "2 inteiros e mais 3 partes". Também é adjetivo ("a praça inteira"): ambíguo.
  inteiro: { sg: 'inteiro', pl: 'inteiros', g: 'm', ambiguo: true },
  trecho: { sg: 'trecho', pl: 'trechos', g: 'm' },
  par: { sg: 'par', pl: 'pares', g: 'm' },
  dica: { sg: 'dica', pl: 'dicas', g: 'f' },
  controle: { sg: 'controle', pl: 'controles', g: 'm' },
  codigo: { sg: 'código', pl: 'códigos', g: 'm' },
  variante: { sg: 'variante', pl: 'variantes', g: 'f' },
  habilidade: { sg: 'habilidade', pl: 'habilidades', g: 'f' },
  tentativa: { sg: 'tentativa', pl: 'tentativas', g: 'f' },
} as const satisfies Record<string, Nome>;

export type NomeId = keyof typeof NOMES;

/**
 * As chaves de um gênero só, para quando a prosa em volta já assume esse gênero.
 *
 * Existe porque há frases que a API não cobre — pronomes, "dessas", "das 9" — e que um
 * item escreve à mão contando com um gênero. Com o campo tipado assim, trocar a peça
 * por uma palavra do outro gênero deixa de compilar, em vez de imprimir "a chance de
 * ela ser" sobre um peso.
 */
export type NomeDoGenero<G extends Genero> = {
  [K in NomeId]: (typeof NOMES)[K]['g'] extends G ? K : never;
}[NomeId];

export interface Adjetivo {
  readonly ms: string;
  readonly fs: string;
  readonly mp: string;
  readonly fp: string;
  /**
   * Adjetivo também disputa papel: a urna dizia "pretas/brancas" no texto e
   * "escuras/claras" na figura para as mesmas bolas.
   */
  readonly grupo?: Grupo;
}

/** Regular em -o: inteiro / inteira / inteiros / inteiras. */
const reg = (ms: string): Adjetivo => ({
  ms,
  fs: `${ms.slice(0, -1)}a`,
  mp: `${ms}s`,
  fp: `${ms.slice(0, -1)}as`,
});

/** Duas formas só, iguais nos dois gêneros: igual/iguais, simples/simples. */
const inv = (sg: string, pl: string): Adjetivo => ({ ms: sg, fs: sg, mp: pl, fp: pl });

export const ADJETIVOS = {
  inteiro: reg('inteiro'),
  inclinado: reg('inclinado'),
  ampliado: reg('ampliado'),
  reduzido: reg('reduzido'),
  desenhado: reg('desenhado'),
  // Particípio: concorda como adjetivo, e é assim que a voz passiva se escreve.
  acrescentado: reg('acrescentado'),
  coberto: reg('coberto'),
  sozinho: reg('sozinho'),
  pedido: reg('pedido'),
  proximo: reg('próximo'),
  impresso: reg('impresso'),
  colado: reg('colado'),
  construido: reg('construído'),
  posicionado: reg('posicionado'),
  repetido: reg('repetido'),
  possivel: inv('possível', 'possíveis'),
  // Não é adjetivo, mas concorda como um: "nenhum polígono", "nenhuma casa".
  nenhum: { ms: 'nenhum', fs: 'nenhuma', mp: 'nenhuns', fp: 'nenhumas' },
  marcado: reg('marcado'),
  fechado: reg('fechado'),
  claro: { ...reg('claro'), grupo: 'cor-clara' },
  escuro: { ...reg('escuro'), grupo: 'cor-escura' },
  branco: { ...reg('branco'), grupo: 'cor-clara' },
  preto: { ...reg('preto'), grupo: 'cor-escura' },
  liso: reg('liso'),
  pintado: reg('pintado'),
  certo: reg('certo'),
  errado: reg('errado'),
  vertical: inv('vertical', 'verticais'),
  horizontal: inv('horizontal', 'horizontais'),
  igual: inv('igual', 'iguais'),
  regular: inv('regular', 'regulares'),
} as const satisfies Record<string, Adjetivo>;

export type AdjetivoId = keyof typeof ADJETIVOS;

/**
 * Só terceira pessoa, e só o número. A chave carrega o tempo, porque aqui não há
 * conjugador: `mudar.futpret` é uma entrada, não uma derivação.
 */
export interface Verbo {
  readonly sg: string;
  readonly pl: string;
}

export const VERBOS = {
  'ser.pres': { sg: 'é', pl: 'são' },
  'ser.pret': { sg: 'foi', pl: 'foram' },
  'pesar.pres': { sg: 'pesa', pl: 'pesam' },
  'fechar.pres': { sg: 'fecha', pl: 'fecham' },
  'faltar.pret': { sg: 'faltou', pl: 'faltaram' },
  'dever.imperf': { sg: 'devia', pl: 'deviam' },
  'ser.imperf': { sg: 'era', pl: 'eram' },
  'funcionar.pres': { sg: 'funciona', pl: 'funcionam' },
  'estar.pres': { sg: 'está', pl: 'estão' },
  'ter.pres': { sg: 'tem', pl: 'têm' },
  'medir.pres': { sg: 'mede', pl: 'medem' },
  'valer.pres': { sg: 'vale', pl: 'valem' },
  'ficar.pres': { sg: 'fica', pl: 'ficam' },
  'mudar.futpret': { sg: 'mudaria', pl: 'mudariam' },
  'crescer.pret': { sg: 'cresceu', pl: 'cresceram' },
  'diminuir.pret': { sg: 'diminuiu', pl: 'diminuíram' },
  'escolher.pret': { sg: 'escolheu', pl: 'escolheram' },
  'sair.pret': { sg: 'saiu', pl: 'saíram' },
  'ficar.pret': { sg: 'ficou', pl: 'ficaram' },
  // Apassivação pronominal: o sujeito vem depois do verbo, e é onde se erra sempre.
  'andar.pres.se': { sg: 'anda-se', pl: 'andam-se' },
} as const satisfies Record<string, Verbo>;

export type VerboId = keyof typeof VERBOS;
